<template>
  <AdminLayout>
    <Head title="Customer" />

    <div class="headline flex flex-col-reverse md:flex-row justify-between items-start mx-4 sm:mx-12">
      <h2
        class="text-lg sm:text-2xl font-semibold pb-5 pt-5 text-blue-600 flex flex-col md:flex-row items-start md:items-end"
      >
        <span class="flex flex-col -mt-5">
          <span class="page-type text-sm sm:text-base">Customer</span>
          <span v-if="customer.name">
            {{ customer.name }}
          </span>
          <span v-else> #{{ customer.id }} </span>
        </span>
      </h2>

      <div class="relative inline-flex shadow-sm rounded-md md:items-start items-end self-end md:self-auto mt-2">
        <button
          v-if="editing"
          @click="toggleEdit()"
          class="inline-flex items-center px-4 py-2 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>

        <button
          v-if="editing"
          @click="save()"
          type="button"
          class="inline-flex items-center px-4 py-2 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Save Changes
        </button>

        <Menu
          as="div"
          class="relative inline-block text-left"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-blue-600"
            >
              Actions
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div>
                <MenuItem v-slot="{ active }">
                  <a
                    @click.prevent="toggleEdit()"
                    href="#"
                    :class="['text-gray-700 hover:text-gray-200 hover:bg-blue-600 group flex items-center p-3 text-sm']"
                  >
                    <PencilIcon
                      class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-200"
                      aria-hidden="true"
                    />
                    Edit
                  </a>
                </MenuItem>
              </div>
              <div
                class="py-1"
                v-if="isAdmin && novaRoute"
              >
                <MenuItem v-slot="{ active }">
                  <NovaMenuButton :nova-route="novaRoute" />
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </div>

    <div
      class="tabs flex-1 flex flex-col"
      :class="{
        'overflow-hidden': pageLayout.shouldLock.value
      }"
    >
      <dl
        class="mb-5 flex sm:rounded-lg bg-gray-100 overflow-y-hidden overflow-x-scroll shadow divide-gray-400 divide-x sm:mx-12"
      >
        <div
          v-for="item in stats"
          :key="item.name"
          class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6"
        >
          <dt class="text-base font-semibold text-blue-600">
            {{ item.name }}
          </dt>
          <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
            <div class="flex items-baseline text-2xl font-semibold text-blue-600">
              {{ item.stat }}
              <span
                v-if="item.previousStat"
                class="ml-2 text-sm font-medium text-gray-600"
              >
                from {{ item.previousStat }}
              </span>
            </div>

            <div
              v-if="item.change && item.changeType"
              :class="[
                item.changeType === 'increase' ? 'bg-green-200 text-green-800' : 'bg-red-100 text-red-800',
                'inline-flex items-baseline px-2.5 py-0.5 rounded-full text-sm font-medium md:mt-2 lg:mt-0'
              ]"
            >
              <ArrowSmUpIcon
                v-if="item.changeType === 'increase'"
                class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-green-400"
                aria-hidden="true"
              />
              <ArrowSmDownIcon
                v-else
                class="-ml-1 mr-0.5 shrink-0 self-center h-5 w-5 text-red-400"
                aria-hidden="true"
              />
              <span class="sr-only"> {{ item.changeType === 'increase' ? 'Increased' : 'Decreased' }} by </span>
              {{ item.change }}
            </div>
          </dd>
        </div>
      </dl>

      <PageTabGroup>
        <PageTabList>
          <PageTab label="Details" />
          <PageTab label="Medical" />
          <PageTab label="Orders" />
          <PageTab label="Loyalty" />
          <PageTab label="Payment Methods" />
          <PageTab
            v-if="hasPermission('view_activity')"
            label="Activity Log"
          />
        </PageTabList>

        <PageTabPanels>
          <PageTabPanel>
            <div class="md:grid md:grid-cols-2 md:gap-10">
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5 mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Customer Details</h3>
                </div>
                <dl class="divide-y divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Full name</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.name || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group mb-3"
                      >
                        <label> First Name </label>
                        <input
                          v-model="form.first_name"
                          type="text"
                        />
                        <p
                          v-if="getErrors('first_name', form)"
                          class="input-error"
                        >
                          {{ getErrors('first_name', form) }}
                        </p>
                      </div>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <label> Last Name </label>
                        <input
                          v-model="form.last_name"
                          type="text"
                        />
                        <p
                          v-if="getErrors('last_name', form)"
                          class="input-error"
                        >
                          {{ getErrors('last_name', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Email Address</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.email || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.email"
                          type="email"
                        />
                        <p
                          v-if="getErrors('email', form)"
                          class="input-error"
                        >
                          {{ getErrors('email', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Phone Number</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.phone || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.phone"
                          type="text"
                          placeholder="555-555-5555"
                          v-maska="'+1 (###) ###-####'"
                        />
                        <p
                          v-if="getErrors('phone', form)"
                          class="input-error"
                        >
                          {{ getErrors('phone', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Birthday</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.birthdate ? _date(customer.birthdate) : '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.birthdate"
                          type="text"
                          placeholder="01/01/1900"
                          v-maska="'##/##/####'"
                        />
                        <p
                          v-if="getErrors('birthdate', form)"
                          class="input-error"
                        >
                          {{ getErrors('birthdate', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">License ID</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.license_number || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.license_number"
                          type="text"
                          placeholder="License ID"
                        />
                        <p
                          v-if="getErrors('license_number', form)"
                          class="input-error"
                        >
                          {{ getErrors('license_number', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Gender</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.gender || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.gender"
                                value="male"
                                name="gender"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="gender"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Male
                              </label>
                            </div>
                            <div class="flex items-center">
                              <input
                                v-model="form.gender"
                                value="female"
                                name="gender"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="gender"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Female
                              </label>
                            </div>
                            <div class="flex items-center">
                              <input
                                v-model="form.gender"
                                value="other"
                                name="gender"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="gender"
                                class="block font-medium text-gray-700 px-3"
                              >
                                N/A
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('gender', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('gender', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Member Since</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      {{ _date(customer.created_at) }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Status</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span
                        v-if="!editing"
                        :class="{
                          'text-green-400': customer.status === 'active',
                          'text-red-400': customer.status !== 'active'
                        }"
                      >
                        {{ capitalize(customer.status) }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <select v-model="form.status">
                          <option
                            v-for="(status, index) in statusOptions"
                            key="index"
                            :value="status"
                          >
                            {{ capitalize(status) }}
                          </option>
                        </select>
                        <p
                          v-if="getErrors('status', form)"
                          class="input-error"
                        >
                          {{ getErrors('status', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Loyalty Member</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.has_loyalty ? 'Yes' : 'No' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.has_loyalty"
                                :value="true"
                                name="loyalty"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="loyalty"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Yes
                              </label>
                            </div>
                            <div class="flex items-center">
                              <input
                                v-model="form.has_loyalty"
                                :value="false"
                                name="loyalty"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="loyalty"
                                class="block font-medium text-gray-700 px-3"
                              >
                                No
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('has_loyalty', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('has_loyalty', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Veteran?</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.is_veteran ? 'Yes' : 'No' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.is_veteran"
                                :value="true"
                                name="male"
                                type="checkbox"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="male"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Veteran
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('is_veteran', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('is_veteran', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Employee?</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.is_employee ? 'Yes' : 'No' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.is_employee"
                                :value="true"
                                name="is_employee"
                                type="checkbox"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="is_employee"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Employee
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('is_employee', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('is_employee', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Industry Discount?</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.has_industry_discount ? 'Yes' : 'No' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.has_industry_discount"
                                :value="true"
                                type="checkbox"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="male"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Industry Discount
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('has_industry_discount', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('has_industry_discount', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                </dl>
              </div>
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5 mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Address</h3>
                </div>
                <dl class="divide-y divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Street 1</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.address?.street_1 || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.address.street_1"
                          type="text"
                          placeholder="Street 1"
                        />
                        <p
                          v-if="getErrors('address.street_1', form)"
                          class="input-error"
                        >
                          {{ getErrors('address.street_1', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Street 2</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.address?.street_2 || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.address.street_2"
                          type="text"
                          placeholder="Street 2"
                        />
                        <p
                          v-if="getErrors('address.street_2', form)"
                          class="input-error"
                        >
                          {{ getErrors('address.street_2', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">City</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.address?.city || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.address.city"
                          type="text"
                          placeholder="City"
                        />
                        <p
                          v-if="getErrors('address.city', form)"
                          class="input-error"
                        >
                          {{ getErrors('address.city', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">State</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.address?.state || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <select
                          id="state"
                          v-model="form.address.state"
                          name="state"
                        >
                          <option :value="null">Select a state</option>
                          <option
                            v-for="(state, key) in states"
                            :value="key"
                          >
                            {{ state }}
                          </option>
                        </select>
                        <p
                          v-if="getErrors('address.state', form)"
                          class="input-error"
                        >
                          {{ getErrors('address.state', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Postal Code</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 md:text-left">
                      <span v-if="!editing">
                        {{ customer.address?.zip || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.address.zip"
                          type="text"
                          placeholder="Zip"
                        />
                        <p
                          v-if="getErrors('address.zip', form)"
                          class="input-error"
                        >
                          {{ getErrors('address.zip', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div class="mt-4">
              <div class="input-group col-span-12 md:col-span-12">
                <label
                  for="product-description"
                  class="text-sm font-medium text-blue-600 mb-1"
                >
                  Notes
                </label>
                <textarea
                  class="text-sm"
                  v-model="form.notes"
                  rows="5"
                  type="text"
                  :disabled="!editing"
                />
                <p
                  v-if="getErrors('notes', form)"
                  class="input-error"
                >
                  {{ getErrors('notes', form) }}
                </p>
              </div>
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:grid md:grid-cols-2 md:gap-10">
              <!-- Basic medical details -->
              <div>
                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5 mb-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Basic Info</h3>
                </div>
                <dl class="divide-y divide-gray-200">
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Medical Source</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      <span v-if="!editing">
                        {{ customer.medical?.display_source || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <select
                          v-model="form.medical.source"
                          id="medical_source"
                        >
                          <option :value="null">Source</option>
                          <option
                            v-for="source in medicalSources"
                            :key="source.id"
                            :value="source.name"
                          >
                            {{ source.display_name }}
                          </option>
                        </select>
                        <p
                          v-if="getErrors('medical.source', form)"
                          class="input-error"
                        >
                          {{ getErrors('medical.source', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Patient #</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      <span v-if="!editing">
                        {{ customer.medical?.card_number || '-' }}
                      </span>
                      <div
                        v-if="editing"
                        class="input-group"
                      >
                        <input
                          v-model="form.medical.card_number"
                          type="text"
                        />
                        <p
                          v-if="getErrors('medical.card_number', form)"
                          class="input-error"
                        >
                          {{ getErrors('medical.card_number', form) }}
                        </p>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Med/Rec</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      <div class="input-group">
                        <fieldset>
                          <div class="flex">
                            <div class="flex items-center">
                              <input
                                v-model="form.is_recreational"
                                :value="false"
                                :disabled="!editing"
                                name="is_recreational"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="is_recreational"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Medical
                              </label>
                            </div>
                            <div class="flex items-center">
                              <input
                                v-model="form.is_recreational"
                                :value="true"
                                :disabled="!editing"
                                name="is_recreational"
                                type="radio"
                                class="focus:ring-blue-600 h-5 w-5 text-blue-600 border-gray-400"
                              />
                              <label
                                for="is_recreational"
                                class="block font-medium text-gray-700 px-3"
                              >
                                Recreational
                              </label>
                            </div>
                          </div>
                          <p
                            v-if="getErrors('is_recreational', form)"
                            class="mt-2 text-sm text-red-600"
                          >
                            {{ getErrors('is_recreational', form) }}
                          </p>
                        </fieldset>
                      </div>
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Patient Type</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ customer.medical?.patient_type ? customer.medical?.patient_type : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Patient Status</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ customer.medical?.display_status ? customer.medical?.display_status : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Physician Name</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ customer.medical?.physician_name ? customer.medical?.physician_name : '-' }}
                    </dd>
                  </div>
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Expiration Date</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ customer.medical?.expiration_date ? _date(customer.medical?.expiration_date) : '-' }}
                    </dd>
                  </div>
                  <!--                                            <div class="py-1 md:py-3 grid grid-cols-2 md:grid-cols-3 gap-4 md:px-2">-->
                  <!--                                                <dt class="text-sm font-medium text-blue-600 mb-1">-->
                  <!--                                                    Monthly Allotment-->
                  <!--                                                </dt>-->
                  <!--                                                <dd class="mt-1 text-sm text-gray-900 md:mt-0 md:col-span-2 text-right">-->
                  <!--                                                    {{ customer.medical?.total_allotment ? _decimals(customer.medical?.total_allotment) + " g" : "-" }}-->
                  <!--                                                </dd>-->
                  <!--                                            </div>-->
                  <!--                                            <div class="py-1 md:py-3 grid grid-cols-2 md:grid-cols-3 gap-4 md:px-2">-->
                  <!--                                                <dt class="text-sm font-medium text-blue-600 mb-1">-->
                  <!--                                                    Remaining Allotment-->
                  <!--                                                </dt>-->
                  <!--                                                <dd class="mt-1 text-sm text-gray-900 md:mt-0 md:col-span-2 text-right">-->
                  <!--                                                    {{ customer.medical?.remaining_allotment ? _decimals(customer.medical?.remaining_allotment) + " g" : "-" }}-->
                  <!--                                                </dd>-->
                  <!--                                            </div>-->
                  <!--                                            <div class="py-1 md:py-3 grid grid-cols-2 md:grid-cols-3 gap-4 md:px-2">-->
                  <!--                                                <dt class="text-sm font-medium text-blue-600 mb-1">-->
                  <!--                                                    Daily Dose-->
                  <!--                                                </dt>-->
                  <!--                                                <dd class="mt-1 text-sm text-gray-900 md:mt-0 md:col-span-2 text-right">-->
                  <!--                                                    {{ customer.medical?.daily_dose ? _decimals(customer.medical?.daily_dose) + " g" : "-" }}-->
                  <!--                                                </dd>-->
                  <!--                                            </div>-->
                  <div class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2">
                    <dt class="text-sm font-medium text-blue-600 mb-1">Term Expiration</dt>
                    <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                      {{ customer.medical?.term_expiration ? _date(customer.medical?.term_expiration) : '-' }}
                    </dd>
                  </div>
                </dl>
              </div>
              <!-- END: Basic medical details -->

              <!-- Routes and aggregates -->
              <div>
                <!--                                <div class="md:border-b md:border-gray-400 md:pb-5 md:mb-5 pt-5 md:pt-0">-->
                <!--                                    <h3 class="text-lg leading-6 font-semibold text-blue-600">-->
                <!--                                        35-Day Aggregates-->
                <!--                                    </h3>-->
                <!--                                </div>-->
                <!--                                <dl class="divide-y divide-gray-200">-->
                <!--                                    <div class="py-1 md:py-3 grid grid-cols-2 md:grid-cols-3 gap-4 md:px-2">-->
                <!--                                        <dt class="text-sm font-medium text-blue-600 mb-1">-->
                <!--                                            Smoking-->
                <!--                                        </dt>-->
                <!--                                        <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2 text-right">-->
                <!--                                            {{ customer.medical?.smoking_aggregate !== null ? toOz(customer.medical?.smoking_aggregate) + " / " + _decimals(customer.medical?.smoking_aggregate) + " g" : "-" }}-->
                <!--                                        </dd>-->
                <!--                                    </div>-->
                <!--                                </dl>-->
                <div class="md:border-b md:border-gray-400 md:pb-5 mb-5 pt-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Medical Routes</h3>
                </div>
                <dl class="divide-y divide-gray-200">
                  <div
                    v-for="(route, index) in medicalRoutes"
                    class="py-1 md:py-3 grid grid-cols-2 md:grid-cols-3 gap-4 md:px-2"
                  >
                    <dt class="text-sm font-medium text-blue-600 mb-1">
                      {{ route.name }}
                    </dt>
                    <dd
                      v-if="route.unit === 'g'"
                      class="text-sm text-gray-900 md:mt-0 md:col-span-2 text-right"
                    >
                      {{
                        route.remaining_allotment !== null
                          ? inGrams(route.remaining_allotment) + ' / ' + toMg(route.remaining_allotment)
                          : '-'
                      }}
                    </dd>
                    <dd
                      v-else
                      class="text-sm text-gray-900 md:mt-0 md:col-span-2 text-right"
                    >
                      {{ route.remaining_allotment !== null ? route.remaining_allotment + ' ' + route.unit : '-' }}
                    </dd>
                  </div>
                </dl>
                <div class="md:border-b md:border-gray-400 md:pb-5 mb-5 pt-5">
                  <h3 class="text-lg leading-6 font-semibold text-blue-600">Caregivers</h3>
                </div>
                <div
                  v-for="(caregiver, index) in form.medical.caregivers"
                  class="py-2 md:py-3 md:grid md:grid-cols-3 gap-4 md:px-2"
                >
                  <dt class="text-sm font-medium text-blue-600 mb-1">Caregiver #{{ index + 1 }}</dt>
                  <dd class="text-sm text-gray-900 md:mt-0 md:col-span-2">
                    <span v-if="!editing">
                      {{ caregiver.card_number || caregiver.name }}
                    </span>
                    <div
                      v-if="editing"
                      class="input-group"
                    >
                      <input
                        v-model="form.medical.caregivers[index].card_number"
                        type="text"
                        placeholder="Caregiver Patient ID"
                      />
                      <p
                        v-if="getErrors(`medical.caregivers.${index}.card_number`, form)"
                        class="input-error"
                      >
                        {{ getErrors(`medical.caregivers.${index}.card_number`, form) }}
                      </p>
                    </div>
                    <div
                      v-if="editing"
                      class="input-group mt-4"
                    >
                      <input
                        v-model="form.medical.caregivers[index].name"
                        type="text"
                        placeholder="Caregiver Name"
                      />
                      <p
                        v-if="getErrors(`medical.caregivers.${index}.name`, form)"
                        class="input-error"
                      >
                        {{ getErrors(`medical.caregivers.${index}.name`, form) }}
                      </p>
                    </div>
                    <div
                      v-if="editing"
                      class="input-group mt-4"
                    >
                      <input
                        v-model="form.medical.caregivers[index].expires_at"
                        type="text"
                        placeholder="Caregiver Medical Exp Date"
                      />
                      <p
                        v-if="getErrors(`medical.caregivers.${index}.expires_at`, form)"
                        class="input-error"
                      >
                        {{ getErrors(`medical.caregivers.${index}.expires_at`, form) }}
                      </p>
                    </div>
                  </dd>
                </div>
              </div>
              <!-- END: Routes and aggregates -->
            </div>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:border-b md:border-gray-400 md:pb-5 mb-5">
              <h3 class="text-lg leading-6 font-semibold text-blue-600">Orders</h3>
            </div>

            <data-table
              :items="customer.orders"
              :headers="['Order #', 'Origin', 'Date', 'Items', 'Total', 'Status', 'Fulfillment Status']"
              :actions="[]"
              :selectable="false"
            >
              <template v-slot:rows="slotRow">
                <order-index-row
                  v-slot:rows="slotRow"
                  v-bind="slotRow"
                  :selectable="false"
                  :show-route="showOrderRoute"
                />
              </template>
            </data-table>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:border-b md:border-gray-400 md:pb-5 mb-5">
              <h3 class="text-lg leading-6 font-semibold text-blue-600">Loyalty Discounts</h3>
            </div>

            <data-table
              :items="loyaltyRewards"
              :headers="['ID', 'Name', 'Points', 'Discount Amount', 'Discount Rate', 'Min Price', 'Max Price']"
              :actions="[]"
              :selectable="false"
            >
              <template v-slot:rows="slotRow">
                <reward-index-row
                  v-slot:rows="slotRow"
                  v-bind="slotRow"
                  :selectable="false"
                />
              </template>
            </data-table>
          </PageTabPanel>

          <PageTabPanel>
            <div class="md:border-b md:border-gray-400 md:pb-5 mb-5">
              <h3 class="text-lg leading-6 font-semibold text-blue-600">Payment Methods</h3>
            </div>

            <data-table
              :items="customer.payment_methods || []"
              :headers="['ID', 'Source', 'Type', 'Sub-Type', 'Name', 'Account', 'Is Default?']"
              :actions="[]"
              :selectable="false"
            >
              <template v-slot:rows="slotRow">
                <payment-method-index-row
                  v-slot:rows="slotRow"
                  v-bind="slotRow"
                  :selectable="false"
                />
              </template>
            </data-table>
          </PageTabPanel>

          <PageTabPanel
            without-padding
            locked-height
          >
            <ActivityLog
              :resource-type="LOG_TYPES.CUSTOMER"
              :resource-id="customer.id"
            />
          </PageTabPanel>
        </PageTabPanels>
      </PageTabGroup>
    </div>
  </AdminLayout>
</template>

<script setup>
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { useLayoutState } from '@/Layouts/layout-state'
import { PageTabGroup, PageTabList, PageTab, PageTabPanels, PageTabPanel } from '@/Components/PageTabs'
import capitalize from '../../Filters/Capitalize'
import NovaMenuButton from '@/Components/Buttons/NovaMenuButton.vue'

const pageLayout = useLayoutState()
const { context } = useInitGridState()
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, Link } from '@inertiajs/vue3'
import { Switch } from '@headlessui/vue'
import { Menu, MenuButton, MenuItem, MenuItems, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import {
  ArchiveIcon,
  ArrowCircleRightIcon,
  ChevronDownIcon,
  DuplicateIcon,
  HeartIcon,
  PencilAltIcon,
  TrashIcon,
  UserAddIcon
} from '@heroicons/vue/solid'
import { QrcodeIcon } from '@heroicons/vue/outline'
import { ChevronUpIcon, ArrowSmDownIcon, ArrowSmUpIcon, PencilIcon } from '@heroicons/vue/solid'
import DataTable from '@/Components/Table/DataTable'
import OrderIndexRow from '@/Pages/Customers/Components/OrderIndexRow'
import RewardIndexRow from '@/Pages/Customers/Components/RewardIndexRow'
import PaymentMethodIndexRow from '@/Pages/Customers/Components/PaymentMethodIndexRow'
import BasicButton from '@/Components/Buttons/BasicButton'
import _date from '@/Filters/Date'
import _datetime from '@/Filters/DateTime'
import _dollars from '@/Filters/Dollars'
import _decimals from '@/Filters/Decimals'
import getsErrors from '@/Mixins/getsErrors'
import { gramsToOunces } from '@/Filters/Conversions'
import ActivityLog from '@/Components/ActivityLog/ActivityLog.vue'
import { LOG_TYPES } from '@/Components/ActivityLog/log-types'
import hasAuth from '@/Mixins/hasAuth'

export default {
  components: {
    ActivityLog,
    AdminLayout,
    Head,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    ArchiveIcon,
    ArrowCircleRightIcon,
    ChevronDownIcon,
    DuplicateIcon,
    HeartIcon,
    PencilAltIcon,
    TrashIcon,
    UserAddIcon,
    QrcodeIcon,
    Switch,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    ChevronUpIcon,
    ArrowSmDownIcon,
    ArrowSmUpIcon,
    DataTable,
    OrderIndexRow,
    RewardIndexRow,
    PaymentMethodIndexRow,
    BasicButton,
    PencilIcon
  },

  mixins: [hasAuth, getsErrors],

  props: {
    customer: {
      type: Object,
      required: true
    },
    /**
     * A list of simple integration objects that act as medical sources.
     * e.g. [ { id: 1, name: 'metrc' } ]
     */
    medicalSources: {
      type: Array,
      default() {
        return []
      }
    },
    statusOptions: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      LOG_TYPES,
      editing: false,
      form: this.$inertia.form({
        first_name: this.customer.first_name,
        last_name: this.customer.last_name,
        email: this.customer.email,
        phone: this.customer.phone,
        birthdate: this.customer.display_birthdate, // ? _date(this.customer.birthdate) : this.customer.birthdate,
        status: this.customer.status,
        gender: this.customer.gender,
        license_number: this.customer.license_number,
        is_veteran: this.customer.is_veteran,
        is_employee: this.customer.is_employee,
        is_recreational: this.customer.is_recreational,
        has_loyalty: this.customer.has_loyalty,
        has_industry_discount: this.customer.has_industry_discount,
        medical: {
          source: this.customer.medical?.source,
          card_number: this.customer.medical?.card_number,
          caregivers: this.customer.medical?.caregivers || []
        },
        address: {
          street_1: this.customer.address?.street_1,
          street_2: this.customer.address?.street_2,
          city: this.customer.address?.city,
          state: this.customer.address?.state,
          zip: this.customer.address?.zip
        },
        notes: this.customer.notes
      }),
      stats: []
    }
  },

  created() {
    let loyaltyStat = this.customer.has_loyalty ? 'Yes' : 'No'
    let allotment = this.remainingAllotment

    if (this.remainingAllotment !== '-') {
      allotment += ' g'
    }

    allotment += ' / ' + this.totalAllotment

    if (this.totalAllotment !== '-') {
      allotment += ' g'
    }

    if (this.loyaltyAccount) {
      loyaltyStat = this.loyaltyAccount.balance + ' pts'
    }

    this.stats.push({ name: 'Purchases', stat: this.customer.order_count })
    this.stats.push({ name: 'Loyalty', stat: loyaltyStat })
    this.stats.push({ name: 'Allotment (remaining / monthly)', stat: allotment })
  },

  computed: {
    states() {
      return this.$page.props.util.states
    },
    showOrderRoute() {
      return (item) => {
        return route('orders.show', { order: item.id })
      }
    },
    remainingAllotment() {
      if (this.customer.remaining_allotment === null) {
        return '-'
      }

      const value = this.customer.remaining_allotment || 0

      return parseFloat(value + '').toFixed(1)
    },
    totalAllotment() {
      if (this.customer.total_allotment === null) {
        return '-'
      }

      const value = this.customer.total_allotment || 0

      return parseFloat(value + '').toFixed(1)
    },
    loyaltyAccount() {
      const accounts = this.customer.loyalty_accounts || []

      return accounts.length > 0 ? accounts[0] : null
    },
    loyaltyRewards() {
      return this.loyaltyAccount?.rewards || []
    },
    medical() {
      return this.customer.medical || null
    },
    medicalRoutes() {
      return this.medical?.routes || []
    },
    novaRoute() {
      return route('nova.pages.detail', {
        resource: 'customers',
        resourceId: this.customer.id
      })
    }
  },

  methods: {
    _date,
    _datetime,
    _dollars,
    _decimals,
    toOz(inGrams) {
      return _decimals(gramsToOunces(inGrams)) + ' oz'
    },
    toMg(inGrams) {
      return (inGrams * 1000).toLocaleString('en-US') + ' mg'
    },
    inGrams(inGrams) {
      return _decimals(inGrams) + ' g'
    },
    showCustomerRoute(id) {
      return route('customers.show', { customer: id })
    },
    toggleEdit() {
      this.editing = !this.editing
    },
    save() {
      this.form.put(route('customers.update', { customer: this.customer.id }), {
        onSuccess: (response) => {
          this.editing = false
        }
      })
    }
  }
}
</script>
